import React, { useEffect, useState } from 'react';
import { Row, Col, Card, ButtonGroup, Button, Table, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import constants from '../../const.js';
import { RANGES, Kpi, BarChart, bucketUnit, bucketDaily, fillHourly } from './sceneHistoryDashboard.js';

const RankList = ({ title, rows, render }) => (
    <Card className="h-100 shadow-sm">
        <Card.Header>{title}</Card.Header>
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <Table size="sm" hover className="mb-0">
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i}>
                            <td className="text-muted small" style={{ width: 24 }}>{i + 1}</td>
                            <td className="text-truncate" style={{ maxWidth: 200 }}>{render(r)}</td>
                            <td className="text-right">{r.searches}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    </Card>
);

const SearchHistoryDashboard = () => {
    const [days, setDays] = useState(30);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [userFilter, setUserFilter] = useState(null);
    const [siteFilter, setSiteFilter] = useState(null);
    const [userLimit, setUserLimit] = useState(10);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;
        let cancelled = false;
        const query = new URLSearchParams({ days, userLimit });
        if (userFilter) query.set('user', userFilter);
        if (siteFilter) query.set('site', siteFilter.id);
        setLoading(true);
        axios.get(`${constants.USER_API.USER_API_URL}/admin/history/search-stats?${query}`, {
            headers: { 'Authorization': token }
        }).then(res => { if (!cancelled) setStats(res.data); })
            .catch(err => console.log(err))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [days, userFilter, siteFilter, userLimit]);

    const selectUser = (username) => setUserFilter(f => f === username ? null : username);
    const selectSite = (site) => {
        if (site.siteId) setSiteFilter(f => f?.id === site.siteId ? null : { id: site.siteId, name: site.site });
    };

    const t = stats?.totals || {};
    const unit = bucketUnit(days);
    const daily = stats ? bucketDaily(stats.daily.map(r => ({ date: r.date, views: r.searches })), days) : [];
    const hourly = stats ? fillHourly(stats.hourly.map(r => ({ hour: r.hour, views: r.searches }))) : [];
    const searches = Number(t.searches) || 0;

    return (
        <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="mb-0">Search History {loading ? <Spinner animation="border" size="sm" /> : null}</h3>
                <ButtonGroup size="sm">
                    {RANGES.map(r => (
                        <Button key={r} variant={r === days ? 'primary' : 'outline-primary'} onClick={() => setDays(r)}>
                            {r === 'all' ? 'All time' : `${r}d`}
                        </Button>
                    ))}
                </ButtonGroup>
            </div>

            {userFilter || siteFilter ? (
                <div className="mb-3">
                    <span className="text-muted small mr-2">Breakdown for</span>
                    {userFilter ? (
                        <Button size="sm" variant="info" className="mr-2" onClick={() => setUserFilter(null)}>
                            User: {userFilter} &times;
                        </Button>
                    ) : null}
                    {siteFilter ? (
                        <Button size="sm" variant="info" className="mr-2" onClick={() => setSiteFilter(null)}>
                            Site: {siteFilter.name} &times;
                        </Button>
                    ) : null}
                    <Button size="sm" variant="link" onClick={() => { setUserFilter(null); setSiteFilter(null); }}>Clear</Button>
                </div>
            ) : null}

            <Row className="mb-3">
                <Col xs={6} lg={3} className="mb-2"><Kpi label="Searches" value={stats ? searches : '-'} /></Col>
                <Col xs={6} lg={3} className="mb-2"><Kpi label="Users" value={t.users ?? '-'} /></Col>
                <Col xs={6} lg={3} className="mb-2">
                    <Kpi label="Text searches" value={t.textSearches ?? '-'}
                        sub={searches ? `${Math.round((Number(t.textSearches) / searches) * 100)}% of searches` : null} />
                </Col>
                <Col xs={6} lg={3} className="mb-2">
                    <Kpi label="Result pages" value={t.pages ?? '-'}
                        sub={searches ? `avg ${(Number(t.pages) / searches).toFixed(1)} / search` : null} />
                </Col>
            </Row>

            <Row className="mb-3">
                <Col lg={8} className="mb-2">
                    <Card className="h-100 shadow-sm">
                        <Card.Header>{unit === 'day' ? 'Daily' : unit === 'week' ? 'Weekly' : 'Monthly'} searches</Card.Header>
                        <Card.Body>
                            <BarChart data={daily} labelKey="date" valueKey="views"
                                formatLabel={k => unit === 'week' ? `Week of ${k}` : k}
                                xLabel={daily.length <= 31 ? (k => unit === 'month' ? k.slice(2) : k.slice(8)) : null} />
                            <div className="d-flex justify-content-between text-muted small mt-1">
                                <span>{daily[0]?.date}</span><span>{daily[daily.length - 1]?.date}</span>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={4} className="mb-2">
                    <Card className="h-100 shadow-sm">
                        <Card.Header>Searches by hour</Card.Header>
                        <Card.Body>
                            <BarChart data={hourly} labelKey="hour" valueKey="views" formatLabel={h => `${h}:00`} xLabel={h => h} />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col lg={4} md={6} className="mb-3">
                    <RankList title="Top search terms" rows={stats?.topTerms || []}
                        render={r => <>{r.term}<div className="text-muted small">{r.users} users</div></>} />
                </Col>
                <Col lg={4} md={6} className="mb-3">
                    <RankList title="Top tags" rows={stats?.topTags || []} render={r => r.name || `#${r.id}`} />
                </Col>
                <Col lg={4} md={6} className="mb-3">
                    <RankList title="Top models" rows={stats?.topModels || []}
                        render={r => <Link to={`/actor/${r.id}`}>{r.name || `#${r.id}`}</Link>} />
                </Col>
                <Col lg={4} md={6} className="mb-3">
                    <Card className="h-100 shadow-sm">
                        <Card.Header>Top users</Card.Header>
                        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                            <Table size="sm" hover className="mb-0">
                                <tbody>
                                    {(stats?.topUsers || []).map((u, i) => (
                                        <tr key={u.username || i} onClick={() => u.username && selectUser(u.username)}
                                            className={u.username === userFilter ? 'table-info' : ''} style={{ cursor: 'pointer' }}>
                                            <td className="text-muted small" style={{ width: 24 }}>{i + 1}</td>
                                            <td>{u.username}<div className="text-muted small">{u.lastSeen}</div></td>
                                            <td className="text-right">{u.searches}<div className="text-muted small">{u.pages} pages</div></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        {(stats?.topUsers || []).length >= userLimit && !userFilter ? (
                            <Card.Footer className="text-center py-1">
                                <Button size="sm" variant="link" onClick={() => setUserLimit(l => l + 25)}>Show more</Button>
                            </Card.Footer>
                        ) : null}
                    </Card>
                </Col>
                <Col lg={4} md={6} className="mb-3">
                    <Card className="h-100 shadow-sm">
                        <Card.Header>Top sites</Card.Header>
                        <Card.Body className="py-2">
                            {(stats?.topSites || []).map(s => {
                                const pct = searches ? (s.searches / searches) * 100 : 0;
                                return (
                                    <div key={s.siteId ?? s.site} className={`mb-2 px-1 rounded ${siteFilter?.id === s.siteId ? 'bg-light' : ''}`}
                                        onClick={() => selectSite(s)} style={{ cursor: s.siteId ? 'pointer' : 'default' }}>
                                        <div className="d-flex justify-content-between small">
                                            <span className="text-truncate">{s.site || `#${s.siteId}`}</span><span>{s.searches}</span>
                                        </div>
                                        <div className="progress" style={{ height: 6 }}>
                                            <div className="progress-bar" style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={4} md={6} className="mb-3">
                    <RankList title="Sort usage" rows={stats?.sorts || []} render={r => r.sort || 'default'} />
                </Col>
            </Row>
        </div>
    );
};

export default SearchHistoryDashboard;
